<template>
  <el-container>
    <el-main class="edit-flat-deal">
      <LoadingBlock :loading="loading" />

      <template v-if="!loading && item">
        <div v-if="showCreatedBanner" class="created-banner">
          <div class="created-banner__icon">
            <el-icon><CircleCheck /></el-icon>
          </div>
          <div>
            <div class="created-banner__title">Deal successfully created</div>
            <div class="created-banner__subtitle">
              Set up your creative and submit for review
            </div>
          </div>
        </div>

        <div class="deal-card">
          <div class="deal-card__header">
            Deal Summary<span
              v-if="item.deal_name"
              class="deal-card__header-name"
              >{{ item.deal_name }}</span
            >
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Website</span>
            <span class="deal-row__value deal-row__value--bold">{{
              item.domain
            }}</span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Spot</span>
            <span class="deal-row__value deal-row__value--bold">{{
              spotsLabel
            }}</span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Period</span>
            <span class="deal-row__value deal-row__value--bold">{{
              periodLabel
            }}</span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Stream</span>
            <span class="deal-row__value">
              <el-tag size="small" class="tag-stream">{{ streamLabel }}</el-tag>
            </span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Ad type</span>
            <span class="deal-row__value">
              <el-tag size="small" type="danger" class="tag-adtype">{{
                adTypeLabel
              }}</el-tag>
            </span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Device</span>
            <span class="deal-row__value">
              <el-tag size="small" class="tag-device">{{ deviceLabel }}</el-tag>
            </span>
          </div>
          <div class="deal-row">
            <span class="deal-row__label">Target country</span>
            <span class="deal-row__value deal-row__value--bold">{{
              countriesLabel
            }}</span>
          </div>
        </div>

        <div class="deal-card deal-card--price">
          <div class="deal-card__header">Deal Price</div>
          <div class="deal-price-row">
            <span
              >CPM: <strong>${{ item.flat_rate }}</strong></span
            >
            <span class="deal-price-dot">·</span>
            <span
              >Daily avg: <strong>{{ formattedDailyAvg }} imps</strong></span
            >
            <span class="deal-price-dot">·</span>
            <span
              >~<strong
                >{{ formattedTotalImps }} total ({{
                  item.total_days
                }}
                days)</strong
              ></span
            >
          </div>
          <div class="deal-expected-price">
            <span>Expected price</span>
            <span class="deal-expected-price__amount"
              >${{ item.total_price }}</span
            >
          </div>
        </div>

        <div class="deal-actions">
          <router-link
            to="/campaigns"
            class="el-button el-button--primary is-plain deal-actions__btn deal-actions__btn--primary"
          >
            <span>Go to Campaigns</span>
          </router-link>
          <router-link
            to="/direct_deals"
            class="el-button is-plain deal-actions__btn"
          >
            <span>Back to Marketplace</span>
          </router-link>
        </div>
      </template>
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { computed, defineComponent, watch, onMounted } from 'vue'
import { CircleCheck } from '@element-plus/icons-vue'

import LoadingBlock from '@/components/LoadingBlock.vue'
import { useCountriesStore } from '@/store/modules/countries'
import { useFlatDealsStore } from '@/apps/traforama/store/flatDeals'
import useRouter from '@/hooks/useRouter'
import { DspAdTypeLabels, StreamLabels, DeviceTypeLabels } from '@/api/types'

const formatImpressions = (value: number): string => {
  if (!value) return '0'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

export default defineComponent({
  name: 'EditFlatDeal',
  components: {
    LoadingBlock,
    CircleCheck
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const flatDealsStore = useFlatDealsStore()
    const countriesStore = useCountriesStore()
    countriesStore.GetItems()
    const { route } = useRouter()

    const item = computed(() => flatDealsStore.current)
    const loading = computed(
      () => flatDealsStore.itemsLoading[props.id] || false
    )
    const showCreatedBanner = computed(
      () => route.value?.query?.created === '1'
    )

    const streamLabel = computed(() => {
      const found = StreamLabels.find((s) => s.stream === item.value?.stream)
      return found?.label || item.value?.stream || ''
    })

    const adTypeLabel = computed(() => {
      const found = DspAdTypeLabels.find(
        (t) => t.type_id === item.value?.type_id
      )
      return found?.label || item.value?.type_id || ''
    })

    const spotsLabel = computed(() => {
      const ids = item.value?.spot_ids
      if (!ids || ids.length === 0) return 'All'
      return ids.join(', ')
    })

    const deviceLabel = computed(() => {
      const ids = item.value?.device_ids
      if (!ids || ids.length === 0) return 'All'
      return ids
        .map(
          (id: number) =>
            DeviceTypeLabels.find((d) => d.type_id === id)?.label || id
        )
        .join(', ')
    })

    const countriesLabel = computed(() => {
      const codes = item.value?.country_codes
      if (!codes || codes.length === 0) return 'Worldwide'
      const found = countriesStore.byIso(codes)
      if (found.length === 0) return codes.join(', ')
      return found.map((c: any) => c.attributes.name).join(', ')
    })

    const periodLabel = computed(() => {
      const start = item.value?.start
      const stop = item.value?.stop
      if (!start || !stop) return ''
      return `${start} – ${stop}`
    })

    const formattedDailyAvg = computed(() =>
      formatImpressions(item.value?.avg_daily_impressions || 0)
    )

    const formattedTotalImps = computed(() => {
      const daily = item.value?.avg_daily_impressions || 0
      const days = item.value?.total_days || 0
      return formatImpressions(daily * days)
    })

    const fetchItem = (id: string) => flatDealsStore.FetchItem(id, true)

    watch(() => props.id, fetchItem)
    onMounted(() => fetchItem(props.id))

    return {
      loading,
      item,
      showCreatedBanner,
      streamLabel,
      adTypeLabel,
      spotsLabel,
      deviceLabel,
      countriesLabel,
      periodLabel,
      formattedDailyAvg,
      formattedTotalImps
    }
  }
})
</script>

<style lang="scss" scoped>
.edit-flat-deal {
  max-width: 700px;
  margin: 0 auto;
  padding: 24px 16px;
}

.created-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f0faf0;
  border: 1px solid #b7e0b7;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;

  &__icon {
    color: #4caf50;
    font-size: 22px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  &__title {
    font-weight: 600;
    font-size: 15px;
    color: #2d6a2d;
  }

  &__subtitle {
    font-size: 13px;
    color: #4a7c4a;
    margin-top: 2px;
  }
}

.deal-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  background: #fff;

  &__header {
    font-weight: 600;
    font-size: 15px;
    padding: 14px 20px;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;

    &-name {
      font-weight: 400;
      color: #666;
      margin-left: 8px;

      &::before {
        content: '·';
        margin-right: 8px;
      }
    }
  }

  &--price {
    .deal-card__header {
      border-bottom: 1px solid #e0e0e0;
    }
  }

  &--form {
    padding: 20px;
  }
}

.deal-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &__label {
    color: #888;
    font-size: 14px;
    width: 160px;
    flex-shrink: 0;
  }

  &__value {
    font-size: 14px;

    &--bold {
      font-weight: 600;
    }
  }
}

.tag-stream {
  background: #ede7f6;
  color: #6a1b9a;
  border-color: #d1c4e9;
}

.tag-adtype {
  background: #fce4ec;
  color: #c62828;
  border-color: #f8bbd0;
}

.tag-device {
  background: #f5f5f5;
  color: #444;
  border-color: #e0e0e0;
}

.deal-price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 14px 20px;
  font-size: 14px;
  color: #444;
}

.deal-price-dot {
  color: #bbb;
  font-size: 16px;
}

.deal-expected-price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #4a90d9;
  color: #fff;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 500;

  &__amount {
    font-size: 20px;
    font-weight: 700;
  }
}

.deal-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  &__btn {
    flex: 1;
    justify-content: center;
    height: 44px;
    font-size: 14px;
    font-weight: 500;

    &--primary {
      background: #3a85d1;
      color: #fff;
      border-color: #3a85d1;

      &:hover {
        background: #2d6fb5;
        border-color: #2d6fb5;
      }
    }
  }
}
</style>
